/**
 * PobockaForm — pobočka **create** surface (`/pujcovna/sprava/pobocky/nova`). Entities are created on
 * one full page (AGENTS.md editing pattern); editing is `PobockaEditModal`. Same form body for both.
 * Admin-only, like the API; a lower level sees the same 403 state as the Správa page.
 */

import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { AppRail } from '@/components/AppRail';
import { Breadcrumb } from '@/components/Breadcrumb';
import { Button } from '@/components/Button';
import { Crown } from '@/components/Crown';
import { DisplayHeading } from '@/components/DisplayHeading';
import { EmptyState } from '@/components/EmptyState';
import { FormPanel, SectionHeader } from '@/components/FormPanel';
import { Icon } from '@/components/Icon';
import { Savebar } from '@/components/Savebar';
import { Shell } from '@/components/Shell';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import { useModuleAccess } from '@/lib/permissions';
import { useCreatePobockaMutation, usePobockaNavrhyQuery } from './api';
import { POBOCKA_FORM_ID, PobockaFormBody, pobockaSeed, pobockaToBody, type PobockaValue } from './pobocka-section-forms';

export function PobockaForm() {
  const navigate = useNavigate();
  const { can, resolved } = useModuleAccess();
  const create = useCreatePobockaMutation();
  const navrhy = usePobockaNavrhyQuery(resolved && can('pujcovna', 'admin'));
  const saving = create.isPending;

  // Stable empty seed (a fresh object per render would re-seed the body in a loop).
  const seed = useMemo(() => pobockaSeed(), []);
  // Only what the Savebar shows re-renders the page.
  const [nazev, setNazev] = useState('');

  const back = () => navigate({ to: '/pujcovna/sprava' });

  // Runs on the form's own submit (the Savebar button targets it by form id, and Enter in a field
  // submits it too), so native validation such as the e-mail type check applies.
  const submit = (form: PobockaValue) => {
    if (!form.nazev.trim()) {
      toast.error('Zadejte název pobočky.');
      return;
    }
    create.mutate(pobockaToBody(form), {
      onSuccess: () => {
        toast.ok('Pobočka vytvořena.');
        back();
      },
      onError: (err) => toast.error(`Chyba: ${(err as ApiError).message ?? 'Nepodařilo se vytvořit pobočku.'}`),
    });
  };

  const denied = resolved && !can('pujcovna', 'admin');

  return (
    <Shell rail={<AppRail active="pujcovna" />}>
      <main className="flex min-w-0 flex-col overflow-auto">
        <Crown>
          <Breadcrumb
            segments={[
              { label: 'Půjčovna', href: '/pujcovna' },
              { label: 'Správa', href: '/pujcovna/sprava' },
              { label: 'Nová pobočka' },
            ]}
          />
          <Crown.Row className="mt-3">
            <DisplayHeading>
              Nová <em>pobočka.</em>
            </DisplayHeading>
          </Crown.Row>
        </Crown>

        {denied ? (
          <div data-testid="pobocka-form-denied" className="px-8 pb-8 pt-5">
            <EmptyState
              eyebrow="403"
              title="Pobočky půjčovny zakládá správce půjčovny."
              description="Pro přístup požádejte správce firmy."
            />
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-6 px-8 py-8">
              <FormPanel>
                <SectionHeader
                  number={1}
                  title={<>Název <em>a kontakty</em>.</>}
                  description="Na veřejném webu se pobočka zobrazí, až bude mít odkaz na webu, aspoň jeden otevřený den a vůz zařazený na web."
                />
                <PobockaFormBody
                  initial={seed}
                  busy={saving}
                  navrhy={navrhy.data}
                  onSubmit={submit}
                  onChange={(v) => setNazev((n) => (n === v.nazev ? n : v.nazev))}
                />
              </FormPanel>
            </div>

            <Savebar
              meta={[
                { k: 'Režim', v: 'Nový záznam' },
                { k: 'Pobočka', v: nazev || '—' },
              ]}
              actions={
                <>
                  <Button variant="secondary" icon={<Icon name="chevronL" size={12} />} onClick={back} disabled={saving}>
                    Zrušit
                  </Button>
                  <Button variant="ink" type="submit" form={POBOCKA_FORM_ID} disabled={saving}>
                    Vytvořit pobočku
                  </Button>
                </>
              }
            />
          </>
        )}
      </main>
    </Shell>
  );
}
